import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import CookieConsent from './components/CookieConsent';
import { initGA, trackPageView } from './utils/analytics';
import './App.css';

// Scroll to top on route change and track page views
const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top instantly on route change
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });

    // Track page view in Google Analytics
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
};

// SEO Meta updater component
const MetaUpdater = () => {
  const { t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // Update meta tags
    document.title = t.meta.title;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t.meta.description);
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', t.meta.keywords);
    }
  }, [t, location]);

  return null;
};

function AppContent() {
  return (
    <div className="App">
      <ScrollToTop />
      <Header />
      <MetaUpdater />
      <main>
        <Routes>
          {/* Estonian routes (default) */}
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/kasutustingimused" element={<TermsOfService />} />
          <Route path="/privaatsuspoliitika" element={<PrivacyPolicy />} />
          <Route path="/kupsiste-poliitika" element={<CookiePolicy />} />
          
          {/* English routes */}
          <Route path="/en" element={<Home />} />
          <Route path="/en/services" element={<Services />} />
          <Route path="/en/about" element={<About />} />
          <Route path="/en/contact" element={<Contact />} />
          <Route path="/en/kasutustingimused" element={<TermsOfService />} />
          <Route path="/en/privaatsuspoliitika" element={<PrivacyPolicy />} />
          <Route path="/en/kupsiste-poliitika" element={<CookiePolicy />} />
        </Routes>
      </main>
      <Footer />
      <CookieConsent />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function App() {
  useEffect(() => {
    // Initialize Google Analytics on app mount
    initGA();

    // Re-initialize if user changes cookie consent
    const handleStorageChange = (e) => {
      if (e.key === 'cookieConsent') {
        initGA();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event from cookie consent component
    const handleConsentChange = () => {
      setTimeout(() => initGA(), 100);
    };
    
    window.addEventListener('cookieConsentChanged', handleConsentChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;